import { type ChangePasswordInput, changePasswordSchema, MIN_PASSWORD_LENGTH } from '@hm/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type UseFormReturn, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Field, TextInput } from '~/components/ui/field'
import { FormError } from '~/components/ui/primitives'
import { ApiRequestError, api, messageOf } from '~/lib/api'
import { useT } from '~/lib/i18n'
import { SESSION_KEY } from '~/lib/session'
import { AuthLayout } from './auth-layout'

/**
 * Changing a password happens in two places — this screen, which a new account is
 * held on until it replaces the password it was given, and a modal on Settings.
 * The form lives here, in the module the gate already pulls into the first load,
 * so that reusing it on Settings costs nothing at boot (`components/ui/modal.tsx`).
 */
export function usePasswordChange(onDone?: () => void) {
  const t = useT()
  const queryClient = useQueryClient()
  const form = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  })

  const change = useMutation({
    mutationFn: (input: ChangePasswordInput) => api.post('/api/auth/change-password', input),
    onSuccess: () => {
      toast.success(t('change_password.password_updated'))
      form.reset()
      queryClient.invalidateQueries({ queryKey: SESSION_KEY })
      onDone?.()
    },
    onError: (error) => {
      if (error instanceof ApiRequestError) {
        for (const field of error.fields) {
          form.setError(field.path as keyof ChangePasswordInput, { message: field.message })
        }
      }
    },
  })

  return {
    form,
    pending: change.isPending,
    error: change.error,
    submit: form.handleSubmit((values) => change.mutate(values)),
  }
}

/** The three fields and whatever the server said, without a submit button. */
export function PasswordFields({
  form,
  error,
  temporary,
}: {
  form: UseFormReturn<ChangePasswordInput>
  error: Error | null
  /** The account is still on the password an admin read out to them. */
  temporary?: boolean
}) {
  const t = useT()
  const errors = form.formState.errors
  return (
    <>
      <Field
        label={temporary ? t('common.temporary_password') : t('change_password.current_password')}
        hint={temporary ? t('change_password.the_one_the_office_gave_you') : undefined}
        error={errors.currentPassword?.message}
        required
      >
        {(id) => (
          <TextInput
            id={id}
            type="password"
            autoComplete="current-password"
            // Only where this form owns the screen. In the modal the dialog decides
            // where focus lands, the same as every other modal in the app.
            autoFocus={temporary}
            invalid={Boolean(errors.currentPassword)}
            {...form.register('currentPassword')}
          />
        )}
      </Field>

      <Field
        label={t('change_password.new_password')}
        hint={t('change_password.at_least_minpasswordlength_characters_pick_something', {
          minpasswordlength: MIN_PASSWORD_LENGTH,
        })}
        error={errors.newPassword?.message}
        required
      >
        {(id) => (
          <TextInput
            id={id}
            type="password"
            autoComplete="new-password"
            invalid={Boolean(errors.newPassword)}
            {...form.register('newPassword')}
          />
        )}
      </Field>

      <Field
        label={t('change_password.type_it_again')}
        error={errors.confirmPassword?.message}
        required
      >
        {(id) => (
          <TextInput
            id={id}
            type="password"
            autoComplete="new-password"
            invalid={Boolean(errors.confirmPassword)}
            {...form.register('confirmPassword')}
          />
        )}
      </Field>

      {error instanceof ApiRequestError && error.fields.length === 0 ? (
        <FormError>{messageOf(error)}</FormError>
      ) : null}
    </>
  )
}

/**
 * The forced change (D5). Every other route answers `password_change_required`
 * until this is done, so it stands alone rather than sitting inside the app.
 */
export function ChangePasswordPage() {
  const t = useT()
  const { form, pending, error, submit } = usePasswordChange()

  return (
    <AuthLayout
      title={t('change_password.choose_your_password')}
      subtitle={t('change_password.your_account_was_set_up_with')}
    >
      <form className="space-y-4" onSubmit={submit} noValidate>
        <PasswordFields form={form} error={error} temporary />
        <Button type="submit" size="lg" full loading={pending}>
          {t('common.save_new_password')}
        </Button>
      </form>
    </AuthLayout>
  )
}
